import type { Rating } from "../types/rating";
import type { PersonCredit } from "../types/media";
import type { DateValue } from "../types/data";
import { isUnknownArray, isTimestamp, isRecord } from "../types/data";
import type { QuestCondition, WeeklyQuestCard } from "../types/games";
import type { TmdbCredits, TmdbFilmDetails } from "../apiRequests/tmdbTypes";
import { APP_REQUEST_KEY } from "../secretKeys";
import { getStoredLanguage, getTmdbImageLanguages, getTmdbLanguage } from "../i18n/language";
import {
  compactFilmCredits,
  isDirectorCrewMember
} from "./compactFilmCredits";

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];

export const WEEKLY_QUEST_PERSON_METADATA_VERSION = 7;

const toMillis = (value: unknown) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (isRecord(value) && typeof value.toMillis === "function") return Number(value.toMillis());
  const date = isTimestamp(value) ? value.toDate() : value instanceof Date ? new Date(value) :
    value && (typeof value === "string" || typeof value === "number") ? new Date(value) : null;
  const time = date?.getTime?.();
  return time !== undefined && Number.isFinite(time) ? time : 0;
};

const getRatingTime = (rating: Rating) => toMillis(
  rating?.firstTrackedAt || rating?.firstRatedAt || rating?.ratedAt
);

const getRatingFilmId = (rating: Rating) => String(
  rating?.filmId || rating?.id || rating?.tmdbId || rating?.filmData?.id || ""
).trim();

const getRatingMediaType = (rating: Rating) => {
  const value = rating?.mediaType || rating?.media_type || rating?.filmData?.mediaType;
  if (value === "tv" || value === "movie") return value;
  return rating?.first_air_date || isUnknownArray(rating?.seasons) ? "tv" : "movie";
};

const extractDirectors = (crew: PersonCredit[] = []) => crew.filter(isDirectorCrewMember);

export const getWeeklyQuestRefreshCandidates = ({
  ratings = [],
  selectedQuestIds = [],
  selectedAtByCardId = {},
  weekStart,
  weekEnd
}: { ratings?: Rating[]; selectedQuestIds?: string[]; selectedAtByCardId?: Record<string, DateValue>; weekStart?: DateValue; weekEnd?: DateValue } = {}) => {
  const weekStartTime = toMillis(weekStart);
  const weekEndTime = toMillis(weekEnd);
  const selectedTimes = asArray(selectedQuestIds)
    .map((cardId) => toMillis(selectedAtByCardId?.[cardId]))
    .filter((time) => time > 0);

  if (!selectedTimes.length || !weekStartTime || !weekEndTime) return [];

  const earliestSelectedAt = Math.max(weekStartTime, Math.min(...selectedTimes));
  const unique = new Map<string, Rating>();

  asArray(ratings).forEach((rating: Rating) => {
    if (!rating || rating.active === false) return;
    const filmId = getRatingFilmId(rating);
    const time = getRatingTime(rating);
    if (!filmId || time < earliestSelectedAt || time >= weekEndTime) return;
    unique.set(`${getRatingMediaType(rating)}:${filmId}`, rating);
  });

  return [...unique.values()];
};

export interface RefreshableQuestCard extends Partial<WeeklyQuestCard> { selected?: boolean; completed?: boolean; }

export const hasPersonConditionToRefresh = (cards: RefreshableQuestCard[] = []) => asArray(cards).some((card) =>
  card?.selected &&
  !card?.completed &&
  asArray(card.conditions).some((condition) =>
    ["actor", "director", "composer"].includes(condition?.type)
  )
);

export const hasWeeklyQuestConditionToRefresh = (cards: RefreshableQuestCard[] = []) => asArray(cards).some((card) =>
  card?.selected &&
  !card?.completed &&
  asArray(card.conditions).length > 0
);

const hasArrayItems = (value: unknown) => isUnknownArray(value) && value.length > 0;

export const hasWeeklyQuestPersonMetadata = (rating: Rating = {}) => [
  rating?.cast,
  rating?.credits?.cast,
  rating?.filmData?.cast,
  rating?.filmData?.credits?.cast,
  rating?.fullDataFilm?.cast,
  rating?.fullDataFilm?.credits?.cast,
  rating?.details?.cast,
  rating?.details?.credits?.cast,
  rating?.crew,
  rating?.credits?.crew,
  rating?.filmData?.crew,
  rating?.filmData?.credits?.crew,
  rating?.fullDataFilm?.crew,
  rating?.fullDataFilm?.credits?.crew,
  rating?.details?.crew,
  rating?.details?.credits?.crew,
  rating?.directors,
  rating?.composers
].some(hasArrayItems);

export const needsWeeklyQuestPersonMetadataRefresh = (rating: Rating = {}) =>
  Number(rating?.weeklyQuestMetadataVersion || 0) < WEEKLY_QUEST_PERSON_METADATA_VERSION ||
  !hasWeeklyQuestPersonMetadata(rating);

const hasWeeklyQuestCountries = (rating: Rating = {}) => [
  rating?.originCountry,
  rating?.origin_country,
  rating?.productionCountry,
  rating?.productionCountries,
  rating?.production_countries
].some((value) => asArray(value).length > 0);

const hasWeeklyQuestGenres = (rating: Rating = {}) => [
  rating?.genres,
  rating?.filmGenres,
  rating?.filmData?.genres,
  rating?.fullDataFilm?.genres
].some((value) => asArray(value).length > 0);

const hasWeeklyQuestYear = (rating: Rating = {}) => Boolean(
  rating?.releaseYear ||
  rating?.releaseDate ||
  rating?.release_date ||
  rating?.filmReleaseDate ||
  rating?.first_air_date
);

const conditionNeedsMetadata = (rating: Rating, condition: Partial<QuestCondition> = {}) => {
  if (condition.type === "country") return !hasWeeklyQuestCountries(rating);
  if (condition.type === "genre") return !hasWeeklyQuestGenres(rating);
  if (condition.type === "year" || condition.type === "decade") {
    return !hasWeeklyQuestYear(rating);
  }
  if (["actor", "director", "composer"].includes(condition.type || "")) {
    return !hasWeeklyQuestPersonMetadata(rating);
  }
  return false;
};

export const needsWeeklyQuestMetadataRefresh = (rating: Rating = {}, cards: RefreshableQuestCard[] = []) =>
  Number(rating?.weeklyQuestMetadataVersion || 0) < WEEKLY_QUEST_PERSON_METADATA_VERSION ||
  (
    rating?.weeklyQuestMetadataComplete !== true &&
    asArray(cards).some((card) =>
      card?.selected &&
      !card?.completed &&
      asArray(card.conditions).some((condition) => conditionNeedsMetadata(rating, condition))
    )
  );

export const fetchWeeklyQuestRatingMetadata = async (
  rating: Rating,
  { fetchImpl, language = getStoredLanguage() }: { fetchImpl?: typeof fetch; language?: string } = {}
) => {
  const filmId = getRatingFilmId(rating);
  const mediaType = getRatingMediaType(rating);
  const request = fetchImpl || (typeof window !== "undefined" ? window.fetch?.bind(window) : null);
  if (!filmId || typeof request !== "function") {
    throw new Error("Невозможно обновить данные фильма");
  }

  const url = new URL(`https://api.themoviedb.org/3/${mediaType}/${filmId}`);
  url.searchParams.set("api_key", APP_REQUEST_KEY);
  url.searchParams.set(
    "append_to_response",
    mediaType === "tv" ? "credits,aggregate_credits" : "credits"
  );
  url.searchParams.set("language", getTmdbLanguage(language));
  url.searchParams.set("include_image_language", getTmdbImageLanguages(language));

  const response = await request(url.toString());
  if (!response.ok) throw new Error(`TMDB ${response.status}`);
  const details: Partial<TmdbFilmDetails> & { aggregate_credits?: TmdbCredits } = await response.json();
  const directCredits = details?.credits || {};
  const aggregateCredits = details?.aggregate_credits || {};
  const compactCredits = compactFilmCredits({
    cast: [
      ...asArray(directCredits.cast),
      ...asArray(aggregateCredits.cast)
    ],
    crew: [
      ...asArray(directCredits.crew),
      ...asArray(aggregateCredits.crew)
    ]
  });
  const productionCountries = asArray(details?.production_countries)
    .map((country) => typeof country === "string" ? country : country?.name || country?.iso_3166_1 || country)
    .filter(Boolean);
  const originCountry = asArray(details?.origin_country).filter(Boolean);
  const releaseDate = details?.release_date || details?.first_air_date || "";
  const runtime = Number(details?.runtime) > 0
    ? Number(details.runtime)
    : asArray(details?.episode_run_time).find((value) => Number(value) > 0) || null;

  return {
    filmId,
    mediaType,
    media_type: mediaType,
    ...(details?.title || details?.name ? { title: details.title || details.name } : {}),
    ...(details?.original_title || details?.original_name
      ? { originalTitle: details.original_title || details.original_name }
      : {}),
    ...(releaseDate ? {
      releaseDate,
      filmReleaseDate: releaseDate,
      ...(mediaType === "tv" ? { first_air_date: releaseDate } : {})
    } : {}),
    ...(isUnknownArray(details?.genres) && details.genres.length
      ? { genres: details.genres, filmGenres: details.genres }
      : {}),
    ...(originCountry.length ? { originCountry } : {}),
    ...(productionCountries.length ? { productionCountries } : {}),
    ...(runtime ? { runtime } : {}),
    ...(compactCredits.cast.length ? { cast: compactCredits.cast } : {}),
    ...(compactCredits.crew.length ? {
      crew: compactCredits.crew,
      directors: extractDirectors(compactCredits.crew)
    } : {}),
    weeklyQuestMetadataVersion: WEEKLY_QUEST_PERSON_METADATA_VERSION,
    weeklyQuestMetadataComplete: true,
    weeklyQuestMetadataUpdatedAt: new Date()
  };
};

export const refreshWeeklyQuestRatingMetadata = async (
  ratings: Rating[] = [],
  { fetchImpl, concurrency = 4, language = getStoredLanguage() }: { fetchImpl?: typeof fetch; concurrency?: number; language?: string } = {}
) => {
  const queue = asArray(ratings);
  const refreshed: Awaited<ReturnType<typeof fetchWeeklyQuestRatingMetadata>>[] = [];
  const failed: { rating: Rating; error: unknown }[] = [];
  const batchSize = Math.max(1, Number(concurrency) || 1);

  for (let index = 0; index < queue.length; index += batchSize) {
    const batch = queue.slice(index, index + batchSize);
    const results = await Promise.allSettled(
      batch.map((rating: Rating) => fetchWeeklyQuestRatingMetadata(rating, { fetchImpl, language }))
    );
    results.forEach((result, resultIndex) => {
      if (result.status === "fulfilled") refreshed.push(result.value);
      else failed.push({ rating: batch[resultIndex], error: result.reason });
    });
  }

  return { refreshed, failed };
};
